import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import { FeatureEntity, UserSettings } from '../../../../types';
import { handleErrors } from '../../../utils/errors';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  disableFeature,
  enableFeature,
  UserKeys,
} from '../../../api/users-api';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { useState } from 'react';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  AdministrationRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { useUserSettings } from '../../../utils/use-user-settings';
import { Messages } from '../../../utils/message-constants';
import ChangeLogDialog from '../../../shared/change-log-dialog';

import CardHeader from '@mui/material/CardHeader';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';

interface LabCardProps {
  lab: FeatureEntity;
  featureAccess: string[];
}

export default function LabCardPage({ lab, featureAccess }: LabCardProps) {
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const { createEvent } = useAnalytics();
  const { administrationRoles, changeLogRoles } = useUserSettings();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const isEnabled = featureAccess.includes(lab.featureString);

  const { mutate, isPending } = useMutation({
    mutationFn: (shouldEnable: boolean) => {
      return shouldEnable
        ? enableFeature(lab.featureString, changeLogMessage)
        : disableFeature(lab.featureString, changeLogMessage);
    },
    onSuccess: (_returnedData, shouldEnable) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: shouldEnable
          ? 'Lab was enabled successfully.'
          : 'Lab was disabled successfully.',
      });

      queryClient.setQueryData<UserSettings>(
        [UserKeys.UserSettings],
        (oldData) => {
          if (!oldData) return undefined;

          const featureAccess = shouldEnable
            ? [
                ...(oldData.trustedTester?.featureAccess || []),
                lab.featureString,
              ]
            : oldData.trustedTester?.featureAccess.filter(
                (feature) => feature !== lab.featureString
              ) || [];

          return {
            ...oldData,
            trustedTester: {
              ...(oldData.trustedTester || {}),
              domain: oldData.trustedTester?.domain || '',
              createdDate: oldData.trustedTester?.createdDate || 0,
              creator: oldData.trustedTester?.creator || '',
              featureAccess,
            },
          };
        }
      );

      setOpenChangeLogDialog(false);
      setChangeLogMessage('');
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: isEnabled
          ? 'There was an issue enabling this lab.'
          : 'There was an issue disabling this lab.',
      });
    },
    onSettled: () => {
      const eventName = isEnabled
        ? analytics.administration.labs.add_feature
        : analytics.administration.labs.remove_feature;

      const eventObject = {
        module: ModuleList.modules.administration.name,
        section: ModuleList.modules.administration.sections.logs,
        lab: lab.featureString || '',
      };
      createEvent(eventName, eventObject);
    },
  });

  function handleToggle() {
    const hasChangeLogPermission = isAbleToSeeUIElement(
      changeLogRoles,
      AdministrationRoles.TurnLabsOnOff.value
    );

    if (hasChangeLogPermission) {
      setOpenChangeLogDialog(true);
    } else {
      mutate(!isEnabled);
    }
  }

  const isAbleToToggleLabs = isAbleToSeeUIElement(
    administrationRoles,
    AdministrationRoles.TurnLabsOnOff.value
  );

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: '0 1 calc((100% - 32px) / 3)',
        minWidth: 300,
        maxWidth: 450,
        height: 350,
      }}
    >
      <CardHeader title={lab.featureName} />
      <CardContent sx={{ flexGrow: 1, minHeight: 100, overflowY: 'auto' }}>
        <Typography color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
          {lab.description}
        </Typography>
      </CardContent>
      <CardActions sx={{ justifyContent: 'right', p: 2 }}>
        <Tooltip
          title={
            !isAbleToToggleLabs
              ? Messages.Errors.Permission.Acting(
                  AdministrationRoles.TurnLabsOnOff.name
                )
              : ''
          }
          arrow
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography>Disabled</Typography>
            <Switch
              checked={isEnabled}
              onChange={handleToggle}
              disabled={!isAbleToToggleLabs}
            />
            <Typography>Enabled</Typography>
          </Stack>
        </Tooltip>
      </CardActions>
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          mutate(!isEnabled);
        }}
        mutateLoading={isPending}
      />
    </Card>
  );
}
